import { ExperimentOutlined, ReloadOutlined, SendOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { EvidenceTraceResult, ForecastRow, RecommendRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Row,
  Segmented,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { hq } from '../session';
import { sumWidth } from '@hq/ui-kit';

const RECOMMEND_STATUS: Record<string, { text: string; color: string }> = {
  PENDING: { text: '待确认', color: 'gold' },
  ADOPTED: { text: '已采纳', color: 'green' },
  REJECTED: { text: '已驳回', color: 'default' }
};

type RecommendFilter = 'ALL' | 'PENDING' | 'ADOPTED' | 'REJECTED';

/**
 * AI 中台（规则版，可被模型替换）：需求预测、降本推荐与结论依据（DR-13.9）。
 * 预测/推荐重算需 `wl:purchase:manage`；依据反查需 `wl:analytics:view`。
 */
export default function AiCenter() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  // ---------------- 预测 ----------------
  const [period, setPeriod] = useState<string>(dayjs().add(1, 'month').format('YYYY-MM'));
  const [historyDays, setHistoryDays] = useState(90);
  const [forecastDays, setForecastDays] = useState(30);

  const forecast = useQuery({
    queryKey: ['ai', 'forecast', period],
    placeholderData: keepPreviousData,
    queryFn: () => hq.aiInsights.forecast(period)
  });

  const runForecast = useMutation({
    mutationFn: () => hq.aiInsights.runForecast({ historyDays, forecastDays, periodType: 'MONTH' }),
    onSuccess: async () => {
      message.success('预测已重算');
      await queryClient.invalidateQueries({ queryKey: ['ai', 'forecast'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '重算失败')
  });

  // ---------------- 推荐 ----------------
  const [recommendStatus, setRecommendStatus] = useState<RecommendFilter>('ALL');
  const recommend = useQuery({
    queryKey: ['ai', 'recommend', recommendStatus],
    placeholderData: keepPreviousData,
    queryFn: () => hq.aiInsights.recommend(recommendStatus === 'ALL' ? undefined : recommendStatus)
  });

  const runRecommend = useMutation({
    mutationFn: () => hq.aiInsights.runRecommend(),
    onSuccess: async () => {
      message.success('降本推荐已重算');
      await queryClient.invalidateQueries({ queryKey: ['ai', 'recommend'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '重算失败')
  });

  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'ADOPT' | 'REJECT' }) =>
      hq.aiInsights.confirmRecommend(id, { action }),
    onSuccess: async () => {
      message.success('已记录决策');
      await queryClient.invalidateQueries({ queryKey: ['ai', 'recommend'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '操作失败')
  });

  // ---------------- 依据 ----------------
  const [evidenceBizNo, setEvidenceBizNo] = useState('');
  const [evidence, setEvidence] = useState<EvidenceTraceResult | null>(null);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  const loadEvidence = async (bizNo: string) => {
    if (!bizNo.trim()) {
      message.warning('请输入申领单号');
      return;
    }
    setEvidenceLoading(true);
    setEvidenceError(null);
    try {
      const data = await hq.aiInsights.evidence({ bizType: 'WL_APPLY', bizNo: bizNo.trim() });
      setEvidence(data);
      setEvidenceOpen(true);
    } catch (error) {
      setEvidenceError(error instanceof ApiError ? error.friendlyMessage : String(error));
      setEvidence(null);
      setEvidenceOpen(true);
    } finally {
      setEvidenceLoading(false);
    }
  };

  // ---------------- 问答 ----------------
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState<{ question: string; answer: string; bizNos: string[]; notice?: string | null }[]>([]);

  const chat = useMutation({
    mutationFn: (text: string) => hq.ai.chat({ question: text }),
    onSuccess: (data, text) => {
      setAnswers((prev) => [
        { question: text, answer: data.answer, bizNos: data.traceBizNos ?? [], notice: data.notice },
        ...prev
      ]);
      setQuestion('');
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '提问失败')
  });

  const forecastColumns: ColumnsType<ForecastRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', width: 220, ellipsis: true },
    { title: '期间', dataIndex: 'forecastPeriod', width: 100, render: (value: string | null) => value || '-' },
    {
      title: '预测需求',
      dataIndex: 'forecastQty',
      width: 120,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '预测金额',
      dataIndex: 'forecastAmount',
      width: 140,
      align: 'right',
      render: (value: number) => formatAmount(value)
    },
    {
      title: '可用库存',
      dataIndex: 'availableQty',
      width: 120,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '建议采购',
      dataIndex: 'suggestPurchaseQty',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value > 0 ? <Typography.Text type="danger">{formatQty(value)}</Typography.Text> : formatQty(0)
    },
    {
      title: '置信度',
      dataIndex: 'confidence',
      width: 100,
      align: 'right',
      render: (value: number) => `${Math.round(value * 100)}%`
    },
    { title: '算法', dataIndex: 'method', width: 120, render: (value: string | null) => value || '-' },
    { title: '生成时间', dataIndex: 'generatedAt', width: 150, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const recommendColumns: ColumnsType<RecommendRow> = [
    { title: '原物资', dataIndex: 'materialName', width: 200, ellipsis: true },
    { title: '替代物资', dataIndex: 'recommendName', width: 200, ellipsis: true },
    {
      title: '现价',
      dataIndex: 'currentPrice',
      width: 120,
      align: 'right',
      render: (value: number) => formatAmount(value, 4)
    },
    {
      title: '替代价',
      dataIndex: 'targetPrice',
      width: 120,
      align: 'right',
      render: (value: number) => formatAmount(value, 4)
    },
    {
      title: '降幅',
      dataIndex: 'saveRatio',
      width: 100,
      align: 'right',
      render: (value: number) => <Tag color="green">{(value * 100).toFixed(1)}%</Tag>
    },
    {
      title: '年用量',
      dataIndex: 'annualQty',
      width: 110,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '年节约',
      dataIndex: 'annualSaveAmount',
      width: 140,
      align: 'right',
      render: (value: number) => <Typography.Text type="success">{formatAmount(value)}</Typography.Text>
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string | null) => {
        const meta = RECOMMEND_STATUS[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_value, row) => (
        <Space size={4}>
          {(row.status ?? 'PENDING') === 'PENDING' ? (
            <>
              <Button
                size="small"
                type="link"
                loading={decide.isPending}
                onClick={() =>
                  modal.confirm({
                    title: '采纳该降本推荐？',
                    content: '采纳仅记录决策，替代物资改价仍需走「调价申请」审批，不会自动生效。',
                    okText: '采纳',
                    cancelText: '取消',
                    onOk: () => decide.mutateAsync({ id: row.id, action: 'ADOPT' })
                  })
                }
              >
                采纳
              </Button>
              <Button
                size="small"
                type="link"
                danger
                onClick={() => decide.mutate({ id: row.id, action: 'REJECT' })}
              >
                驳回
              </Button>
            </>
          ) : null}
          <Button
            size="small"
            type="link"
            onClick={() =>
              modal.info({
                title: `依据：${row.materialName ?? ''} → ${row.recommendName ?? ''}`,
                width: 640,
                content: (
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>
                    {row.evidenceJson
                      ? (() => {
                          try {
                            return JSON.stringify(JSON.parse(row.evidenceJson), null, 2);
                          } catch {
                            return row.evidenceJson;
                          }
                        })()
                      : '该推荐未附带依据（evidence_json 为空）'}
                  </pre>
                )
              })
            }
          >
            依据
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          AI 中台
          <Typography.Text type="secondary">需求预测 / 降本推荐 / 结论依据（规则版，可替换为模型）</Typography.Text>
        </Space>
      }
    >
      <Tabs
        items={[
          {
            key: 'forecast',
            label: '需求预测',
            children: (
              <>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Typography.Text>预测期间</Typography.Text>
                  <Input
                    value={period}
                    onChange={(event) => setPeriod(event.target.value)}
                    placeholder="YYYY-MM"
                    style={{ width: 120 }}
                  />
                  <Typography.Text type="secondary">回看天数</Typography.Text>
                  <InputNumber min={7} max={365} value={historyDays} onChange={(v) => setHistoryDays(v ?? 90)} style={{ width: 90 }} />
                  <Typography.Text type="secondary">预测天数</Typography.Text>
                  <InputNumber min={1} max={180} value={forecastDays} onChange={(v) => setForecastDays(v ?? 30)} style={{ width: 90 }} />
                  <Button icon={<ReloadOutlined />} onClick={() => forecast.refetch()} loading={forecast.isFetching}>
                    查询
                  </Button>
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={runForecast.isPending}
                    onClick={() =>
                      modal.confirm({
                        title: '重算需求预测？',
                        content: `将按近 ${historyDays} 天消耗量推算未来 ${forecastDays} 天需求，并覆盖当期已有结果。`,
                        okText: '重算',
                        cancelText: '取消',
                        onOk: () => runForecast.mutateAsync()
                      })
                    }
                  >
                    重算预测
                  </Button>
                </Space>
                {forecast.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="预测数据加载失败"
                    description={forecast.error instanceof ApiError ? forecast.error.friendlyMessage : String(forecast.error)}
                  />
                ) : null}
                <Table<ForecastRow>
                  rowKey="id"
                  size="middle"
                  columns={forecastColumns}
                  dataSource={forecast.data ?? []}
                  loading={forecast.isPending}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  scroll={{ x: sumWidth(forecastColumns) }}
                  locale={{ emptyText: '当期暂无预测结果，可点击「重算预测」生成' }}
                />
              </>
            )
          },
          {
            key: 'recommend',
            label: '降本推荐',
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Segmented<RecommendFilter>
                    value={recommendStatus}
                    options={[
                      { label: '全部', value: 'ALL' },
                      { label: '待确认', value: 'PENDING' },
                      { label: '已采纳', value: 'ADOPTED' },
                      { label: '已驳回', value: 'REJECTED' }
                    ]}
                    onChange={(value) => setRecommendStatus(value)}
                  />
                  <Button icon={<ReloadOutlined />} onClick={() => recommend.refetch()} loading={recommend.isFetching}>
                    刷新
                  </Button>
                  <Button
                    type="primary"
                    icon={<ExperimentOutlined />}
                    loading={runRecommend.isPending}
                    onClick={() =>
                      modal.confirm({
                        title: '重算降本推荐？',
                        content: '将按同品类替代物资价差生成推荐，需采购人逐条确认后采纳。',
                        okText: '重算',
                        cancelText: '取消',
                        onOk: () => runRecommend.mutateAsync()
                      })
                    }
                  >
                    重算推荐
                  </Button>
                </Space>
                {recommend.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="推荐数据加载失败"
                    description={recommend.error instanceof ApiError ? recommend.error.friendlyMessage : String(recommend.error)}
                  />
                ) : null}
                <Table<RecommendRow>
                  rowKey="id"
                  size="middle"
                  columns={recommendColumns}
                  dataSource={recommend.data ?? []}
                  loading={recommend.isPending}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  scroll={{ x: sumWidth(recommendColumns) }}
                  locale={{ emptyText: '暂无降本推荐' }}
                />
              </>
            )
          },
          {
            key: 'evidence',
            label: '结论依据',
            children: (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="按申领单号反查全链路依据（DR-13.9）"
                  description="一次返回申请、审批、判源、配送轨迹、成本转移、结算与预算占用的原始单据，供审计与监管下钻。"
                />
                <Space style={{ marginBottom: 12 }}>
                  <Input
                    placeholder="申领单号，如 SQ202610240001"
                    style={{ width: 280 }}
                    value={evidenceBizNo}
                    onChange={(event) => setEvidenceBizNo(event.target.value)}
                    onPressEnter={() => loadEvidence(evidenceBizNo)}
                  />
                  <Button type="primary" loading={evidenceLoading} onClick={() => loadEvidence(evidenceBizNo)}>
                    查询依据
                  </Button>
                </Space>
                {!evidence && !evidenceError ? <Empty description="输入单号后查询" /> : null}
              </>
            )
          },
          {
            key: 'chat',
            label: '智能问答',
            children: (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="规则引擎取真实数据，大模型就绪后仅做润色"
                  description="可问：本月预算执行率、哪些物资库存预警、某申领单到哪一步了。"
                />
                <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                  <Input
                    placeholder="输入问题"
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    onPressEnter={() => question.trim() && chat.mutate(question.trim())}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={chat.isPending}
                    onClick={() => question.trim() && chat.mutate(question.trim())}
                  >
                    提问
                  </Button>
                </Space.Compact>
                {chat.isPending ? <Spin /> : null}
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {answers.map((item, index) => (
                    <Card key={`${item.question}-${index}`} size="small" title={item.question}>
                      <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                        {item.answer}
                      </Typography.Paragraph>
                      <Space wrap>
                        {item.bizNos.map((bizNo) => (
                          <Button key={bizNo} size="small" type="link" onClick={() => loadEvidence(bizNo)}>
                            {bizNo}
                          </Button>
                        ))}
                        {item.notice ? <Typography.Text type="secondary">{item.notice}</Typography.Text> : null}
                      </Space>
                    </Card>
                  ))}
                </Space>
              </>
            )
          }
        ]}
      />

      <Drawer
        title={evidence ? `依据 ${evidence.trace.bizNo}` : '依据'}
        open={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        width={1000}
      >
        {evidenceError ? (
          <Alert type="error" showIcon message="查询依据失败" description={evidenceError} />
        ) : evidence ? (
          <>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="单号">{evidence.trace.bizNo}</Descriptions.Item>
              <Descriptions.Item label="状态">{evidence.trace.bizStatus}</Descriptions.Item>
              <Descriptions.Item label="判源">{evidence.trace.allocateResult || '-'}</Descriptions.Item>
              <Descriptions.Item label="数量">{formatQty(evidence.trace.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="金额">{formatAmount(evidence.trace.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="紧急度">{evidence.trace.urgentLevel || '-'}</Descriptions.Item>
              <Descriptions.Item label="提交">{formatDisplayTime(evidence.trace.submitAt)}</Descriptions.Item>
              <Descriptions.Item label="审批完成">{formatDisplayTime(evidence.trace.approveAt)}</Descriptions.Item>
              <Descriptions.Item label="关闭">{formatDisplayTime(evidence.trace.closeAt)}</Descriptions.Item>
            </Descriptions>

            <Typography.Title level={5}>申领明细</Typography.Title>
            <Table
              rowKey="lineNo"
              size="small"
              pagination={false}
              dataSource={evidence.items}
              scroll={{ x: 1200 }}
              columns={[
                { title: '行', dataIndex: 'lineNo', width: 60 },
                { title: '物资编码', dataIndex: 'materialCode', width: 140, render: (v: string | null) => v || '-' },
                { title: '物资名称', dataIndex: 'materialName', ellipsis: true },
                { title: '申请量', dataIndex: 'reqQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '批准量', dataIndex: 'approvedQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '判源', dataIndex: 'allocateResult', width: 110, render: (v: string | null) => v || '-' },
                { title: '外仓量', dataIndex: 'outerAllocateQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '缺口', dataIndex: 'stockShortQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '签收量', dataIndex: 'signedQty', width: 100, align: 'right', render: (v: number) => formatQty(v) }
              ]}
            />

            <Typography.Title level={5} style={{ marginTop: 16 }}>
              审批轨迹（{evidence.approval.tasks.length} 个节点）
            </Typography.Title>
            <Table
              rowKey={(row) => `${row.nodeCode}-${row.seq}`}
              size="small"
              pagination={false}
              dataSource={evidence.approval.tasks}
              columns={[
                { title: '节点', dataIndex: 'nodeName', width: 150 },
                { title: '类型', dataIndex: 'nodeType', width: 110, render: (v: string | null) => v || '-' },
                { title: '处理人', dataIndex: 'assigneeName', width: 110, render: (v: string | null) => v || '-' },
                { title: '状态', dataIndex: 'taskStatus', width: 100 },
                { title: '动作', dataIndex: 'action', width: 100, render: (v: string | null) => v || '-' },
                { title: '处理时间', dataIndex: 'handleAt', width: 150, render: (v: string | null) => formatDisplayTime(v) },
                { title: '意见', dataIndex: 'opinion', ellipsis: true, render: (v: string | null) => v || '-' }
              ]}
            />

            <Typography.Title level={5} style={{ marginTop: 16 }}>
              物流与结算
            </Typography.Title>
            <Row gutter={16}>
              <Col span={12}>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={evidence.logistics.deliveries}
                  locale={{ emptyText: '无配送单' }}
                  columns={[
                    { title: '配送单', dataIndex: 'bizNo', width: 170 },
                    { title: '状态', dataIndex: 'deliveryStatus', width: 100 },
                    { title: '签收', dataIndex: 'signAt', width: 150, render: (v: string | null) => formatDisplayTime(v) }
                  ]}
                />
              </Col>
              <Col span={12}>
                <Table
                  rowKey={(row) => `${row.settleNo}-${row.bizNo ?? ''}`}
                  size="small"
                  pagination={false}
                  dataSource={evidence.settlement.items}
                  locale={{ emptyText: '无结算明细' }}
                  columns={[
                    { title: '对账单', dataIndex: 'settleNo', width: 170 },
                    { title: '费用类型', dataIndex: 'feeType', width: 100, render: (v: string | null) => v || '-' },
                    { title: '金额', dataIndex: 'amount', width: 110, align: 'right', render: (v: number) => formatAmount(v) }
                  ]}
                />
              </Col>
            </Row>

            <Typography.Title level={5} style={{ marginTop: 16 }}>
              预算占用 / 成本 / 异常
            </Typography.Title>
            <Space wrap size={8}>
              <Tag color="blue">预算占用 {evidence.budget.length} 条</Tag>
              <Tag color="cyan">成本转移 {evidence.cost.transfers.length} 条</Tag>
              <Tag color={evidence.alerts.length > 0 ? 'red' : 'default'}>异常 {evidence.alerts.length} 条</Tag>
              <Tag color="purple">轨迹 {evidence.logistics.tracks.length} 个节点</Tag>
            </Space>
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
